import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';

// project-imports
import Drawer from './Drawer';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';

import { DRAWER_WIDTH, MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import AdminGuard from 'routes/AdminGuard';
import Header from 'layout/Dashboard/Header';
import useSubAdminPresence from 'hooks/useSubAdminPresence';
import { V2 } from 'layout/Dashboard/Header/headerV2Tokens';

const adminMainBg = `
  radial-gradient(ellipse 80% 50% at 0% -10%, rgba(15, 118, 110, 0.12), transparent 55%),
  radial-gradient(ellipse 60% 40% at 100% 0%, rgba(14, 165, 233, 0.08), transparent 50%),
  linear-gradient(180deg, #eef2f5 0%, #f3f5f7 45%, #eef1f4 100%)
`;

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  useSubAdminPresence();
  const theme = useTheme();

  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);
  if (menuMasterLoading) return <Loader />;

  return (
    <AdminGuard>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          bgcolor: V2.bg,
          fontFamily: V2.fontBody,
        }}
      >
        <Header />
        {!isHorizontal ? <Drawer /> : <HorizontalBar />}

        <Box
          component="main"
          sx={{
            width: `calc(100% - ${DRAWER_WIDTH}px)`,
            flexGrow: 1,
            p: { xs: 2, md: 2.5 },
            pt: 4,
            background: adminMainBg,
            minHeight: '100vh',
          }}
        >
          <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit', mb: isHorizontal ? 2 : 'inherit' }} />
          <Container
            maxWidth={container ? 'xl' : false}
            sx={{
              xs: 0,
              ...(container && { px: { xs: 0, md: 2 } }),
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Breadcrumbs />
            <Outlet />
          </Container>
        </Box>
      </Box>
    </AdminGuard>
  );
}
